import { useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import type { InfoGridField, InfoGridProps } from "./infoGrid.types"
import InfoGridItem from "./InfoGridItem"
import InfoGridToolbar from "./InfoGridToolbar"
import { useInfoGridState } from "./useInfoGridState"

function isFieldEmpty<TData>(
  field: InfoGridField<TData>,
  data: TData | null | undefined,
) {
  if (!data) return true
  const value =
    typeof field.accessor === "function"
      ? field.accessor(data)
      : data[field.accessor]
  return value === null || value === undefined || value === ""
}

function InfoGridCustomizable<TData>(
  props: InfoGridProps<TData> & { id: string },
) {
  const { id, data, fields, isLoading, error, className, itemClassName } = props
  const state = useInfoGridState(id, fields)

  const visibleFields = useMemo(() => {
    if (!state.hideEmpty || isLoading || !data) return state.resolvedFields
    return state.resolvedFields.filter((f) => !isFieldEmpty(f, data))
  }, [state.resolvedFields, state.hideEmpty, isLoading, data])

  // Only replace the whole grid with an error when we have nothing to
  // render. With partial data we show the grid and surface the error
  // as a non-blocking indicator above it.
  if (error && !data && !isLoading) {
    return <ErrorMessage error={error} />
  }

  return (
    <div className="info-grid-container group">
      {/* The way back from a `defaultHidden` field — without it, a field
          flagged hidden could never be read. Revealed on hover so it stays
          out of the panel's reading flow. */}
      <InfoGridToolbar
        className="invisible group-hover:visible"
        state={state}
      />

      {error && (
        <div className="info-grid-error-banner">
          <ErrorMessage error={error} />
        </div>
      )}

      <dl className={cn("info-grid", className)}>
        {visibleFields.map((field) => (
          <InfoGridItem
            key={field.id}
            {...field}
            data={data}
            isLoading={isLoading}
            className={cn(itemClassName, field.className)}
          />
        ))}
      </dl>
    </div>
  )
}

function InfoGrid<TData>(props: InfoGridProps<TData>) {
  const { id, data, fields, isLoading, error, className, itemClassName } = props

  if (id) {
    return <InfoGridCustomizable {...props} id={id} />
  }

  // Same partial-data policy for the uncustomized variant.
  if (error && !data && !isLoading) {
    return <ErrorMessage error={error} />
  }

  return (
    <>
      {error && <ErrorMessage error={error} />}
      <dl className={cn("info-grid", className)}>
        {fields.map((field) => (
          <InfoGridItem
            key={field.id}
            {...field}
            data={data}
            isLoading={isLoading}
            className={cn(itemClassName, field.className)}
          />
        ))}
      </dl>
    </>
  )
}

export default InfoGrid
